import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import maplibregl from 'maplibre-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { tieneCoordenadasValidas, filtrarClientesConCoordenadas } from '../utils/coordenadas';
import { useAuth } from '../contexts/AuthContext';
import RequireRole from './RequireRole';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MapDiv = styled.div`
  width: 100%;
  height: 100%;
`;

const DrawControls = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const DrawButton = styled.button`
  padding: 8px 12px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  &:hover {
    background: #f5f5f5;
  }
  
  &.active {
    background: #007cbf;
    color: white;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  font-size: 1.1rem;
  color: #2c3e50;
`;

// Controles para selección de mapa base (esquin''a superior derecha)
const BaseMapControls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
`;

const BaseMapSelect = styled.select`
  padding: 8px 12px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

// Clave y estilos de MapTiler (se usa la ya presente en el proyecto)
const MAPTILER_KEY = 'IZhoNp6EdEnhhPPNcAMO';

const BASEMAPS = [
  { id: 'streets', label: 'Calles', url: `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}` },
  { id: 'hybrid', label: 'Híbrido (Satélite + etiquetas)', url: `https://api.maptiler.com/maps/hybrid/style.json?key=${MAPTILER_KEY}` },
  { id: 'satellite', label: 'Satélite', url: `https://api.maptiler.com/maps/satellite/style.json?key=${MAPTILER_KEY}` },
  { 
    id: 'osm', 
    label: 'OpenStreetMap (Detallado)', 
    url: {
      version: 8,
      sources: {
        'osm-tiles': {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
          ],
          tileSize: 256,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxzoom: 19,
          minzoom: 0
        }
      },
      layers: [
        {
          id: 'osm-tiles-layer',
          type: 'raster',
          source: 'osm-tiles',
          minzoom: 0,
          maxzoom: 22
        }
      ]
    }
  },
];

const MapContainerComponent = ({ 
  clientes, 
  vendedores, 
  selectedClientes, 
  onClientesSelection, 
  loading,
  onClearSelectionFn, // Prop para exponer la función de limpieza hacia el padre
  onPolygonActiveChange, // Notifica si hay polígonos activos (true/false)
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const fullscreenControl = useRef(null);
  const draw = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [drawMode, setDrawMode] = useState('simple_select');
  const [baseMapId, setBaseMapId] = useState('streets');
  const [polygonActiveLocal, setPolygonActiveLocal] = useState(false);

  // Configurar fuentes de datos del mapa
  const setupMapSources = useCallback(() => {
    console.log('Configurando fuentes de datos del mapa...');
    
    try {
      // Fuente para clientes (sin clustering)
      map.current.addSource('clientes', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      console.log('Source "clientes" agregado');

      // Source para clientes seleccionados
      map.current.addSource('selected-clientes', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      console.log('Source "selected-clientes" agregado');
    } catch (error) {
      console.error('Error configurando fuentes de datos:', error);
    }
  }, []);

  // Configurar capas del mapa (solo puntos individuales)
  const setupMapLayers = useCallback(() => {
    console.log('Configurando capas del mapa...');
    
    try {

      // Agregar imagen para los marcadores
      map.current.loadImage(
        'https://cdn-icons-png.flaticon.com/512/25/25613.png',
        (error, image) => {
          if (error) {
            console.error('Error cargando imagen de marcador:', error);
            return;
          }
          
          // Agregar la imagen al mapa
          if (!map.current.hasImage('store-icon')) {
            map.current.addImage('store-icon', image, { sdf: true });
          }
          
          // Puntos individuales con íconos personalizados
          map.current.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: 'clientes',
            filter: ['!', ['has', 'point_count']],
            layout: {
              'icon-image': 'store-icon',
              'icon-size': 0.1,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true
            },
            paint: {
              'icon-color': ['get', 'vendedorColor'],
              'icon-halo-color': '#ffffff',
              'icon-halo-width': 0.01
            }
          });
          console.log('Capa "unclustered-point" con íconos agregada');
        }
      );

      // Agregar imagen para los marcadores seleccionados
      map.current.loadImage(
        'https://cdn-icons-png.flaticon.com/512/25/25613.png',
        (error, image) => {
          if (error) {
            console.error('Error cargando imagen de marcador seleccionado:', error);
            return;
          }
          
          // Agregar la imagen al mapa
          if (!map.current.hasImage('selected-store-icon')) {
            map.current.addImage('selected-store-icon', image, { sdf: true });
          }
          
          // Clientes seleccionados con íconos personalizados
          map.current.addLayer({
            id: 'selected-points',
            type: 'symbol',
            source: 'selected-clientes',
            layout: {
              'icon-image': 'selected-store-icon',
              'icon-size': 0.12,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true
            },
            paint: {
              'icon-color': '#ff6b6b',
              'icon-halo-color': '#ffffff',
              'icon-halo-width': 0.12
            }
          });
          console.log('Capa "selected-points" con íconos agregada');
        }
      );
      console.log('Todas las capas configuradas exitosamente');
    } catch (error) {
      console.error('Error configurando capas del mapa:', error);
    }
  }, []);

  // Función para centrar el mapa en los clientes
  const centerMapOnClients = useCallback((clientesList) => {
    if (!map.current || clientesList.length === 0) return;
    
    try {
      // Filtrar clientes con coordenadas válidas usando la función de utilidad
      const clientesConCoordenadas = filtrarClientesConCoordenadas(clientesList);
      
      // Crear un array de coordenadas de todos los clientes con coordenadas válidas
      const coordinates = clientesConCoordenadas.map(cliente => [
        parseFloat(cliente.clt_lon),
        parseFloat(cliente.clt_lat)
      ]);
      
      // Si no hay coordenadas válidas, no hacemos nada
      if (coordinates.length === 0) {
        console.warn('No se encontraron coordenadas válidas para centrar el mapa');
        return;
      }
      
      // Crear un feature collection con los puntos
      const points = {
        type: 'FeatureCollection',
        features: coordinates.map(coord => ({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: coord
          }
        }))
      };
      
      // Calcular el bounding box que contiene todos los puntos
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new maplibregl.LngLatBounds(coordinates[0], coordinates[0]));
      
      // Centrar el mapa en el bounding box con padding
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15
      });
      
      console.log('Mapa centrado en los clientes');
    } catch (error) {
      console.error('Error centrando el mapa en los clientes:', error);
    }
  }, []);
  
  // Manejar creación/actualización de polígonos
  const handleDrawCreate = useCallback((e) => {
    try {
      const polygon = e.features[0];
      if (polygon.geometry.type === 'Polygon') {
        console.log('=== DEBUG SELECCIÓN POR POLÍGONO ===');
        console.log('Total de clientes filtrados disponibles:', clientes.length);
        
        // Obtener las features visibles del mapa
        // Esto garantiza que solo seleccionamos puntos que están realmente renderizados
        const mapFeatures = map.current.queryRenderedFeatures(undefined, {
          layers: ['unclustered-point']
        });
        
        console.log('Features renderizadas en el mapa:', mapFeatures.length);
        
        // Filtrar las features que están dentro del polígono
        const featuresEnPoligono = mapFeatures.filter(feature => {
          if (feature.geometry.type === 'Point') {
            return turf.booleanPointInPolygon(feature.geometry, polygon);
          }
          return false;
        });
        
        console.log('Features dentro del polígono:', featuresEnPoligono.length);
        
        // Convertir las features a objetos cliente, preservando el vendedor correcto
        const clientesEnPoligono = featuresEnPoligono.map(feature => {
          try {
            const clienteStr = feature?.properties?.cliente;
            if (clienteStr) {
              return JSON.parse(clienteStr);
            }
            // Fallback: buscar por ID si por alguna razón no vino el JSON embebido
            const clienteId = feature?.properties?.clienteId;
            if (clienteId) {
              return clientes.find(c => c.clt_codigo === clienteId) || null;
            }
            return null;
          } catch (err) {
            console.error('Error procesando feature:', err, feature);
            return null;
          }
        }).filter(cliente => cliente); // Filtrar nulls/undefined
        
        console.log('Clientes seleccionados por polígono:', clientesEnPoligono.length);
        console.log('Detalle de clientes seleccionados:', clientesEnPoligono.map(c => ({ 
          codigo: c.clt_codigo, 
          nombre: c.clt_nombre, 
          vendedor: c.clt_ven_codigo,
          frecuencia: c.clt_frecuencia || 'N/A',
          semanaVisita: c.clt_semanavisita || 'N/A'
        })));
        console.log('=== FIN DEBUG ===');
        
        // Actualizar la selección
        onClientesSelection(clientesEnPoligono);
        // Notificar polígono activo
        if (typeof onPolygonActiveChange === 'function') {
          onPolygonActiveChange(true);
        }
        setPolygonActiveLocal(true);

        // Centrar el mapa en el área del polígono dibujado
        try {
          const bbox = turf.bbox(polygon);
          const bounds = new maplibregl.LngLatBounds(
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]]
          );
          map.current.fitBounds(bounds, { padding: 50, maxZoom: 16 });
        } catch (fitErr) {
          console.warn('No se pudo centrar en el polígono:', fitErr);
        }
      }
    } catch (error) {
      console.error('Error en la selección por polígono:', error);
      onClientesSelection([]);
    }
  }, [clientes, onClientesSelection, onPolygonActiveChange]);

  // Manejar eliminación de polígonos
  const handleDrawDelete = useCallback(() => {
    onClientesSelection([]);
    if (typeof onPolygonActiveChange === 'function') {
      let hasAny = false;
      try {
        const all = draw.current?.getAll?.();
        hasAny = Array.isArray(all?.features) && all.features.some(f => f?.geometry?.type === 'Polygon');
      } catch (e) {
        hasAny = false;
      }
      onPolygonActiveChange(!!hasAny);
    }
    // Actualizar estado local
    try {
      const all = draw.current?.getAll?.();
      const hasAnyLocal = Array.isArray(all?.features) && all.features.some(f => f?.geometry?.type === 'Polygon');
      setPolygonActiveLocal(!!hasAnyLocal);
    } catch {}
  }, [onClientesSelection, onPolygonActiveChange]);

  // Configurar eventos del mapa
  const setupMapEvents = useCallback(() => {
    console.log('Configurando eventos del mapa...');
    
    try {
      // Crear popup para tooltips
      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: '300px'
      });

      // Hover en puntos para mostrar tooltip
      map.current.on('mouseenter', 'unclustered-point', (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        
        const feature = e.features[0];
        const clienteNombre = feature.properties.clienteNombre;
        const clienteRuta = feature.properties.ruta;
        const clienteCategoria = feature.properties.clienteCategoria;
        const vendedorNombre = feature.properties.vendedorNombre;
        const diaVisita = feature.properties.diaVisita;
        const clienteId = feature.properties.clienteId;
        const frecuencia = feature.properties.frecuencia;
        const semanaVisita = feature.properties.semanaVisita;
        
        // Construir el texto de frecuencia
        let frecuenciaText = frecuencia;
        if (frecuencia === 'QUINCENAL' || frecuencia === 'MENSUAL') {
          frecuenciaText += ` (${semanaVisita})`;
        }
        
        popup.setLngLat(e.lngLat)
          .setHTML(`
            <div style="padding: 10px; font-family: Arial, sans-serif; min-width: 200px;">
              <strong style="color: #333; font-size: 14px; display: block; margin-bottom: 8px;">${clienteNombre}</strong>
              <div style="font-size: 12px; color: #555; line-height: 1.4;">
                <div><strong>Código:</strong> ${clienteId}</div>
                <div><strong>Categoría:</strong> ${clienteCategoria || 'Sin categoría'}</div>
                <div><strong>Ruta:</strong> ${clienteRuta}</div>
                <div><strong>Vendedor:</strong> ${vendedorNombre}</div>
                <div><strong>Día de visita:</strong> ${diaVisita}</div>
                <div><strong>Frecuencia:</strong> ${frecuenciaText}</div>
              </div>
            </div>
          `)
          .addTo(map.current);
      });

      // Auto-cerrar tooltip al salir del área
      map.current.on('mouseleave', 'unclustered-point', () => {
        map.current.getCanvas().style.cursor = '';
        popup.remove();
      });

      // Eventos de dibujo
      map.current.on('draw.create', handleDrawCreate);
      map.current.on('draw.update', handleDrawCreate);
      map.current.on('draw.delete', handleDrawDelete);
      
      console.log('Eventos del mapa configurados exitosamente');
    } catch (error) {
      console.error('Error configurando eventos del mapa:', error);
    }
  }, [handleDrawCreate, handleDrawDelete]);

  // Inicializar mapa
  useEffect(() => {
    if (map.current) return;

    console.log('Inicializando mapa...');
    console.log('Contenedor del mapa:', mapContainer.current);

    if (!mapContainer.current) {
      console.error('Contenedor del mapa no disponible');
      return;
    }

    try {
      // Inicializar el mapa sin centro fijo (se calculará dinámicamente)
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: BASEMAPS.find(b => b.id === baseMapId)?.url,
        center: [-66.5897, 6.4238], // Centro aproximado de Venezuela (lng, lat)
        zoom: 6
      });

      console.log('Mapa creado exitosamente');

      // Inicializar controles de dibujo con estilos mejorados
      draw.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {},
        defaultMode: 'simple_select',
        styles: [
          // Estilo para el polígono activo (siendo dibujado)
          {
            id: 'gl-draw-polygon-fill-active',
            type: 'fill',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#3498db',
              'fill-opacity': 0.4
            }
          },
          // Estilo para el borde del polígono activo
          {
            id: 'gl-draw-polygon-stroke-active',
            type: 'line',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            paint: {
              'line-color': '#3498db',
              'line-width': 3,
              'line-dasharray': [0.5, 2]
            }
          },
          // Estilo para el polígono inactivo (ya dibujado)
          {
            id: 'gl-draw-polygon-fill',
            type: 'fill',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#3498db',
              'fill-outline-color': '#3498db',
              'fill-opacity': 0.5
            }
          },
          // Estilo para el borde del polígono inactivo
          {
            id: 'gl-draw-polygon-stroke',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'line-color': '#3498db',
              'line-width': 3
            }
          },
          // Puntos de vértice activos
          {
            id: 'gl-draw-polygon-and-line-vertex-active',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
            paint: {
              'circle-radius': 6,
              'circle-color': '#fff',
              'circle-stroke-color': '#3498db',
              'circle-stroke-width': 2
            }
          }
        ]
      });

      map.current.addControl(draw.current);

      if (!fullscreenControl.current) {
        fullscreenControl.current = new maplibregl.FullscreenControl();
      }
      map.current.addControl(fullscreenControl.current, 'bottom-right');
      console.log('Controles de dibujo agregados');

      map.current.on('load', () => {
        console.log('Mapa cargado completamente');
        setMapLoaded(true);
        setupMapSources();
        setupMapLayers();
        setupMapEvents();

        
        // Centrar el mapa en los clientes después de cargar las capas
        if (clientes.length > 0) {
          centerMapOnClients(clientes);
        }
      });

      map.current.on('error', (e) => {
        console.error('Error en el mapa:', e);
      });

    } catch (error) {
      console.error('Error inicializando mapa:', error);
    }

    return () => {
      if (map.current) {
        console.log('Limpiando mapa...');
        if (fullscreenControl.current) {
          try {
            map.current.removeControl(fullscreenControl.current);
          } catch (err) {
            console.warn('No se pudo remover FullscreenControl:', err);
          }
        }
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // Sin dependencias para evitar re-inicialización

  // Actualizar datos del mapa (solo puntos individuales, sin clustering)
  const updateMapData = useCallback(() => {
    if (!mapLoaded || !map.current) return;

    console.log('Actualizando datos del mapa...', { clientes: clientes.length, vendedores: vendedores.length });

    try {
      // Filtrar solo clientes con coordenadas válidas
      const clientesConCoordenadas = filtrarClientesConCoordenadas(clientes);
      
      // Crear puntos individuales para todos los clientes con coordenadas válidas
      const allPoints = clientesConCoordenadas.map(cliente => {
        const vendedor = vendedores.find(v => v.ven_codigo === cliente.clt_ven_codigo);
        return {
          type: 'Feature',
          properties: {
            cluster: false,
            clienteId: cliente.clt_codigo,
            cliente: JSON.stringify(cliente),
            ruta: cliente.clt_ven_codigo,
            vendedor: JSON.stringify(vendedor || {}),
            vendedorColor: vendedor?.ven_color || '#666666',
            clienteNombre: cliente.clt_nombre,
            clienteCategoria: cliente.clt_categoria ?? '',
            vendedorNombre: vendedor?.ven_nombre || 'Sin asignar',
            diaVisita: cliente.clt_diavisita,
            frecuencia: cliente.clt_frecuencia ?? 'SEMANAL',
            semanaVisita: cliente.clt_semanavisita ?? ''
          },
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(cliente.clt_lon), parseFloat(cliente.clt_lat)]
          }
        };
      });
      
      const source = map.current.getSource('clientes');
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: allPoints
        });
        console.log('Puntos individuales actualizados:', allPoints.length);
      }

      console.log('Datos del mapa actualizados correctamente con puntos individuales');
    } catch (error) {
      console.error('Error actualizando datos del mapa:', error);
    }
  }, [clientes, vendedores, mapLoaded]);

  // Re-agregar fuentes/capas, datos y terreno tras cambiar el estilo del mapa
  const refreshCustomLayers = useCallback(() => {
    if (!map.current) return;
    try {
      setupMapSources();
      setupMapLayers();
      // Datos actuales de clientes
      updateMapData();
      // Re-aplicar clientes seleccionados
      const clientesSeleccionadosConCoordenadas = filtrarClientesConCoordenadas(selectedClientes);
      const selectedFeatures = clientesSeleccionadosConCoordenadas.map(cliente => ({
        type: 'Feature',
        properties: { clienteId: cliente.clt_codigo },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(cliente.clt_lon), parseFloat(cliente.clt_lat)]
        }
      }));
      const selSource = map.current.getSource('selected-clientes');
      if (selSource) {
        selSource.setData({ type: 'FeatureCollection', features: selectedFeatures });
      }
    } catch (error) {
      console.error('Error refrescando capas personalizadas tras cambio de estilo:', error);
    }
  }, [setupMapSources, setupMapLayers, updateMapData, selectedClientes]);

  // Cambiar mapa base dinámicamente
  const handleBaseMapChange = useCallback((e) => {
    const id = e.target.value;
    setBaseMapId(id);
    const base = BASEMAPS.find(b => b.id === id);
    if (map.current && base) {
      map.current.setStyle(base.url);
      // Re-agregar nuestras fuentes y capas cuando el estilo esté listo
      const readd = () => {
        if (!map.current) return;
        if (typeof map.current.isStyleLoaded === 'function' && map.current.isStyleLoaded()) {
          refreshCustomLayers();
        } else {
          map.current.once('idle', () => refreshCustomLayers());
        }
      };
      map.current.once('styledata', readd);
    }
  }, [refreshCustomLayers]);



  

  // Actualizar datos del mapa cuando cambien los clientes o vendedores
  useEffect(() => {
    updateMapData();
    
    // Centrar el mapa en los clientes cuando cambian, salvo que haya un polígono activo
    if (mapLoaded && map.current && clientes.length > 0 && !polygonActiveLocal) {
      centerMapOnClients(clientes);
    }
  }, [updateMapData, clientes, mapLoaded, polygonActiveLocal]);

  // Actualizar clientes seleccionados
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    // Filtrar solo clientes seleccionados con coordenadas válidas
    const clientesSeleccionadosConCoordenadas = filtrarClientesConCoordenadas(selectedClientes);
    
    const selectedFeatures = clientesSeleccionadosConCoordenadas.map(cliente => ({
      type: 'Feature',
      properties: { clienteId: cliente.clt_codigo },
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(cliente.clt_lon), parseFloat(cliente.clt_lat)]
      }
    }));

    map.current.getSource('selected-clientes').setData({
      type: 'FeatureCollection',
      features: selectedFeatures
    });
  }, [selectedClientes, mapLoaded]);

  // Controles de dibujo
  const handleDrawMode = useCallback((mode) => {
    setDrawMode(mode);
    if (draw.current) {
      draw.current.changeMode(mode);
    }
    
    if (mode === 'simple_select') {
      onClientesSelection([]);
    }
  }, [onClientesSelection]);

  const clearSelection = useCallback(() => {
    if (draw.current) {
      draw.current.deleteAll();
    }
    onClientesSelection([]);
    setDrawMode('simple_select');
    if (typeof onPolygonActiveChange === 'function') {
      onPolygonActiveChange(false);
    }
    setPolygonActiveLocal(false);
  }, [onClientesSelection]);
  
  // Exponer la función clearSelection a través de la prop onClearSelection
  // Usamos useEffect con dependencias vacías para que solo se ejecute una vez al montar el componente
  useEffect(() => {
    if (onClearSelectionFn) {
      onClearSelectionFn(() => {
        if (draw.current) {
          draw.current.deleteAll();
        }
        onClientesSelection([]);
        setDrawMode('simple_select');
        if (typeof onPolygonActiveChange === 'function') {
          onPolygonActiveChange(false);
        }
      });
    }
  }, []);  // Sin dependencias para evitar el bucle infinito

  return (
    <MapWrapper>
      <RequireRole roles={['admin','gerente', 'supervisor']}>
        <DrawControls>
          <DrawButton
            className={drawMode === 'draw_polygon' ? 'active' : ''}
            onClick={() => handleDrawMode('draw_polygon')}
            title="Dibujar polígono"
          >
            🖊 Polígono
          </DrawButton>

          <DrawButton
            onClick={clearSelection}
            title="Borrar selección"
          >
            ❌ Borrar
          </DrawButton>
        </DrawControls>
      </RequireRole>

      <BaseMapControls>
        <BaseMapSelect value={baseMapId} onChange={handleBaseMapChange} title="Mapa base">
          {BASEMAPS.map(b => (
            <option key={b.id} value={b.id}>{b.label}</option>
          ))}
        </BaseMapSelect>
      </BaseMapControls>

      <MapDiv ref={mapContainer} />
      
      {(loading || !mapLoaded) && (
        <LoadingOverlay>
          Cargando mapa...
        </LoadingOverlay>
      )}
    </MapWrapper>
  );
};

export default MapContainerComponent;
